import { useCallback, useEffect, useRef, useState } from "react";
import { organizerGetDailyCourtScheduleApi } from "../../../api/api";

const INVALID_RESPONSE_CODE = "INVALID_DAILY_SCHEDULE_RESPONSE";
const SCHEDULE_STATES = new Set([
	"ready",
	"configuration_required",
	"closed",
	"no_active_courts",
]);

const isPlainRecord = (value) => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};

const scheduleFromResponse = (response) => {
	const info = response?.data?.info;
	const hasValidState = isPlainRecord(info) && SCHEDULE_STATES.has(info.state);
	const hasValidBookableState =
		hasValidState && info.bookable === (info.state === "ready");

	if (!hasValidBookableState) {
		const error = new Error(
			"La respuesta de la agenda no tiene un formato válido.",
		);
		error.name = "ScheduleSchemaError";
		error.code = INVALID_RESPONSE_CODE;
		throw error;
	}
	return info;
};

export function useOrganizerDailySchedule({
	clubId,
	date,
	includeCancelled = false,
	enabled = false,
}) {
	const [schedule, setSchedule] = useState(null);
	const [state, setState] = useState("idle");
	const [error, setError] = useState(null);
	const requestIdRef = useRef(0);
	const controllerRef = useRef(null);

	const load = useCallback(
		async ({ propagateError = false } = {}) => {
			if (!enabled || !clubId || !date) return null;
			controllerRef.current?.abort();
			const controller = new AbortController();
			controllerRef.current = controller;
			const requestId = ++requestIdRef.current;
			setState("loading");
			setError(null);
			try {
				const response = await organizerGetDailyCourtScheduleApi(
					{ barId: clubId, date, includeCancelled: Boolean(includeCancelled) },
					{ signal: controller.signal },
				);
				if (requestId !== requestIdRef.current || controller.signal.aborted)
					return null;
				const nextSchedule = scheduleFromResponse(response);
				setSchedule(nextSchedule);
				setState("success");
				return nextSchedule;
			} catch (requestError) {
				if (requestId !== requestIdRef.current || controller.signal.aborted)
					return null;
				setError(requestError);
				setState("error");
				if (propagateError) throw requestError;
				return null;
			}
		},
		[clubId, date, includeCancelled, enabled],
	);

	useEffect(() => {
		if (!enabled || !clubId || !date) {
			controllerRef.current?.abort();
			setSchedule(null);
			setState("idle");
			setError(null);
			return undefined;
		}
		load().catch(() => {});
		return () => controllerRef.current?.abort();
	}, [enabled, clubId, date, load]);

	const reload = useCallback(() => {
		load();
	}, [load]);
	const reloadAndWait = useCallback(
		() => load({ propagateError: true }),
		[load],
	);
	return { schedule, state, error, reload, reloadAndWait };
}
